import { MessageSchema } from "@redotech/redo-model/agents/llm-messages";
import { PageContextSchema } from "@redotech/redo-model/agents/page-context";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { EscalationStatusSchema } from "./escalation";

export const MerchantSupportAgentThreadSchema = z.object({
  triageThreadId: zExt.objectId(),
  currentThreadId: zExt.objectId(),
  newMessages: z.array(MessageSchema),
});

export const messageMerchantSupportAgent = {
  name: "messageMerchantSupportAgent" as const,
  input: z.object({
    messages: z.array(MessageSchema),
    currentThreadId: z.string().optional(),
    triageThreadId: z
      .string()
      .optional()
      .describe("If left undefined, a new thread will be created."),
    userEmail: z
      .string()
      .optional()
      .describe("User email for context when creating a new thread."),
    pageContext: PageContextSchema.optional().describe(
      "Page context object with structured data. Example: { pageDescription: 'User is viewing return #1093', preferredAgent: 'returns-specific', preferredSkill: 'returns', pageData: { returnId: 'abc', orderId: '1093' } }",
    ),
  }),
  output: z.object({ thread: MerchantSupportAgentThreadSchema }),
};

// Simplified message schema for thread history
const ThreadHistoryMessageSchema = z.object({
  id: z.string(),
  role: createEnum(["user", "assistant", "tool"]),
  content: z.union([z.string(), z.array(z.any())]),
});

const ThreadEscalationInfoSchema_ = z.object({
  escalationId: z.string(),
  status: EscalationStatusSchema,
  noticeTopic: z.string(),
  humanJoinedAt: z.date().nullable(),
  closedAt: z.date().nullable(),
});
export const ThreadEscalationInfoSchema: z.ZodType<ThreadEscalationInfo> =
  ThreadEscalationInfoSchema_;
export interface ThreadEscalationInfo
  extends z.infer<typeof ThreadEscalationInfoSchema_> {}

export const MerchantSupportAgentThreadHistorySchema = z.object({
  _id: zExt.objectId(),
  updatedAt: z.date(),
  messages: z.array(ThreadHistoryMessageSchema),
  state: z
    .object({
      identifiers: z.object({ userEmail: z.string() }),
      childThreads: z
        .array(
          z.object({
            agentType: z.string(),
            threadId: z.string(),
            active: z.boolean().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  escalation: ThreadEscalationInfoSchema.optional(),
});

export const getMerchantSupportAgentThreads = {
  name: "getMerchantSupportAgentThreads" as const,
  input: z.object({
    userEmail: z.string().email().describe("User email to filter threads by"),
  }),
  output: z.object({
    threads: z.array(MerchantSupportAgentThreadHistorySchema),
  }),
};

export const getMerchantSupportAgentThread = {
  name: "getMerchantSupportAgentThread" as const,
  input: z.object({ threadId: zExt.objectId() }),
  output: z.object({ thread: MerchantSupportAgentThreadHistorySchema }),
};
